import React, { useState, useEffect } from "react";
import { useCreateModelCapabilityMap, useUpdateModelCapabilityMap, useFetchIndustries } from "../../hooks/useCapabilityMaps";
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from "react-hook-form";

function AddEditCapabilityMapModal({ isOpen, onClose, mode, mapData }) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting }
  } = useForm({
    defaultValues: {
      name: "",
      arabicName: "",
      frenchName: "",
      germanName: "",
      spanishName: "",
      italianName: "",
      industry: "",
      description: "",
      arabicDescription: "",
      frenchDescription: "",
      germanDescription: "",
      spanishDescription: "",
      italianDescription: "",
      versionNumber: "1.0",
      level: "Enterprise",
      entity: "",
      visible: true,
      color: "#10B981"
    }
  });
  
  const [activeTab, setActiveTab] = useState("basic");
  const [phases, setPhases] = useState([]);

  const createMutation = useCreateModelCapabilityMap();
  const updateMutation = useUpdateModelCapabilityMap();
  const { data: industries } = useFetchIndustries();

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && mapData) {
        reset({
          name: mapData.name || "",
          arabicName: mapData.arabicName || "",
          frenchName: mapData.frenchName || "",
          germanName: mapData.germanName || "",
          spanishName: mapData.spanishName || "",
          italianName: mapData.italianName || "",
          industry: mapData.industry || "",
          description: mapData.description || "",
          arabicDescription: mapData.arabicDescription || "",
          frenchDescription: mapData.frenchDescription || "",
          germanDescription: mapData.germanDescription || "",
          spanishDescription: mapData.spanishDescription || "",
          italianDescription: mapData.italianDescription || "",
          versionNumber: mapData.versionNumber || "1.0",
          level: mapData.level || "Enterprise",
          entity: mapData.entity || "",
          visible: mapData.visible !== undefined ? mapData.visible : true,
          color: mapData.color || "#10B981"
        });
    
        setPhases(mapData.phases || []);
      } else {
        reset({
          name: "",
          arabicName: "",
          frenchName: "",
          germanName: "",
          spanishName: "",
          italianName: "",
          industry: "",
          description: "",
          arabicDescription: "",
          frenchDescription: "",
          germanDescription: "",
          spanishDescription: "",
          italianDescription: "",
          versionNumber: "1.0",
          level: "Enterprise",
          entity: "",
          visible: true,
          color: "#10B981",
          phases: []
        });
      }
    }
  }, [isOpen, mode, mapData, reset]);
  
  if (!isOpen) return null;

  const onSubmit = async (data) => {
    const submitData = { ...data, phases };
  
    try {
      if (mode === "add") {
        await createMutation.mutateAsync(submitData);
      } else {
        await updateMutation.mutateAsync({ id: mapData.id, data: submitData });
      }
      onClose();
    } catch (error) {
      console.error("Error saving capability map:", error);
    }
  };

  const addPhase = () => {
    setPhases([...phases, {
      id: uuidv4(),
      title: "",
      arabicTitle: "",
      description: "",
      orderIndex: phases.length + 1,
      color: "#10B981",
      items: []
    }]);
  };

  const updatePhase = (index, field, value) => {
    const updatedPhases = [...phases];
    updatedPhases[index] = { ...updatedPhases[index], [field]: value };
    setPhases(updatedPhases);
  };

  const removePhase = (index) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This phase will be permanently deleted.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        setPhases(prev => prev.filter((_, i) => i !== index));
  
        Swal.fire({
          title: 'Deleted!',
          text: 'The phase has been deleted.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  const addItem = (phaseIndex) => {
    const updatedPhases = [...phases];
    if (!updatedPhases[phaseIndex].items) {
      updatedPhases[phaseIndex].items = [];
    }
    updatedPhases[phaseIndex].items.push({
      id: Date.now() + Math.random(),
      content: "",
      orderIndex: updatedPhases[phaseIndex].items.length + 1,
      visible: true
    });
    setPhases(updatedPhases);
  };

  const updateItem = (phaseIndex, itemIndex, field, value) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].items = [...updatedPhases[phaseIndex].items];
    updatedPhases[phaseIndex].items[itemIndex] = {
      ...updatedPhases[phaseIndex].items[itemIndex],
      [field]: value
    };
    setPhases(updatedPhases);
  };

  const removeItem = (phaseIndex, itemIndex) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].items = updatedPhases[phaseIndex].items.filter((_, i) => i !== itemIndex);
    setPhases(updatedPhases);
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading || isSubmitting;

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="h-2" style={{ backgroundColor: watch("color") }}></div>
        
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
          <h3 className="text-xl font-semibold text-slate-800">
            {mode === "add" ? "Add New Capability Map" : "Edit Capability Map"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-500" disabled={isLoading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="border-b border-slate-200 px-6">
          <nav className="-mb-px flex space-x-6">
            <button
              type="button"
              onClick={() => setActiveTab("basic")}
              className={`py-3 text-sm font-medium border-b-2 ${
                activeTab === "basic"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              disabled={isLoading}
            >
              Basic Information
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("phases")}
              className={`py-3 text-sm font-medium border-b-2 ${
                activeTab === "phases"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              disabled={isLoading}
            >
              Phases & Items
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="overflow-y-auto p-6" style={{ maxHeight: "calc(90vh - 180px)" }}>
            {activeTab === "basic" ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Name (English) *
                    </label>
                    <input
                      type="text"
                      {...register("name", { required: true })}
                      className="form-input w-full"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Name (Arabic)
                    </label>
                    <input
                      type="text"
                      {...register("arabicName")}
                      className="form-input w-full"
                      dir="rtl"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      French Name
                    </label>
                    <input
                      type="text"
                      {...register("frenchName")}
                      className="form-input w-full"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      German Name
                    </label>
                    <input
                      type="text"
                      {...register("germanName")}
                      className="form-input w-full"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Industry *
                    </label>
                    <select
                      {...register("industry", { required: true })}
                      className="form-select w-full"
                      disabled={isLoading}
                    >
                      <option value="">Select Industry</option>
                      {industries?.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Version Number
                    </label>
                    <input
                      type="text"
                      {...register("versionNumber")}
                      className="form-input w-full"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description (English)
                  </label>
                  <textarea
                    {...register("description")}
                    rows="3"
                    className="form-textarea w-full"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description (Arabic)
                  </label>
                  <textarea
                    {...register("arabicDescription")}
                    rows="3"
                    className="form-textarea w-full"
                    dir="rtl"
                    disabled={isLoading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Level
                    </label>
                    <input
                      type="text"
                      {...register("level")}
                      className="form-input w-full"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Entity
                    </label>
                    <input
                      type="text"
                      {...register("entity")}
                      className="form-input w-full"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register("visible")}
                      className="form-checkbox"
                      disabled={isLoading}
                    />
                    <span className="ml-2 text-sm text-slate-700">Visible by default</span>
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-medium text-slate-800">Phases</h4>
                  <button
                    type="button"
                    onClick={addPhase}
                    className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white"
                    disabled={isLoading}
                  >
                    Add Phase
                  </button>
                </div>

                {phases.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <p className="text-slate-500">No phases added yet</p>
                    <button
                      type="button"
                      onClick={addPhase}
                      className="mt-2 text-indigo-500 hover:text-indigo-600"
                      disabled={isLoading}
                    >
                      Add your first phase
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {phases.map((phase, phaseIndex) => (
                      <div key={phase.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h5 className="font-medium text-slate-700">Phase {phaseIndex + 1}</h5>
                          <button
                            type="button"
                            onClick={() => removePhase(phaseIndex)}
                            className="text-rose-400 hover:text-rose-500"
                            disabled={isLoading}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Phase Title (English)
                            </label>
                            <input
                              type="text"
                              value={phase.title || ""}
                              onChange={(e) => updatePhase(phaseIndex, "title", e.target.value)}
                              className="form-input w-full text-sm"
                              placeholder="e.g., Discover"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Arabic Title
                            </label>
                            <input
                              type="text"
                              value={phase.arabicTitle || ""}
                              onChange={(e) => updatePhase(phaseIndex, "arabicTitle", e.target.value)}
                              className="form-input w-full text-sm"
                              dir="rtl"
                              disabled={isLoading}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Color
                            </label>
                            <input
                              type="color"
                              value={phase.color || "#10B981"}
                              onChange={(e) => updatePhase(phaseIndex, "color", e.target.value)}
                              className="w-full h-8 p-1 border border-slate-200 rounded"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Order Index
                            </label>
                            <input
                              type="number"
                              value={phase.orderIndex || phaseIndex + 1}
                              onChange={(e) => updatePhase(phaseIndex, "orderIndex", parseInt(e.target.value) || 1)}
                              className="form-input w-full text-sm"
                              disabled={isLoading}
                            />
                          </div>
                        </div>

                        <div className="mb-3">
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Description
                          </label>
                          <input
                            type="text"
                            value={phase.description || ""}
                            onChange={(e) => updatePhase(phaseIndex, "description", e.target.value)}
                            className="form-input w-full text-sm"
                            placeholder="Phase description"
                            disabled={isLoading}
                          />
                        </div>

                        <div className="mt-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-slate-600">Items</span>
                            <button
                              type="button"
                              onClick={() => addItem(phaseIndex)}
                              className="text-xs text-indigo-500 hover:text-indigo-600"
                              disabled={isLoading}
                            >
                              + Add Item
                            </button>
                          </div>

                          {phase.items && phase.items.length > 0 ? (
                            <div className="space-y-2">
                              {phase.items.map((item, itemIndex) => (
                                <div key={item.id} className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                                  <input
                                    type="text"
                                    value={item.content || ""}
                                    onChange={(e) => updateItem(phaseIndex, itemIndex, "content", e.target.value)}
                                    className="form-input flex-1 text-sm"
                                    placeholder="Item content"
                                    disabled={isLoading}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeItem(phaseIndex, itemIndex)}
                                    className="text-rose-400 hover:text-rose-500"
                                    disabled={isLoading}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400 italic">No items added</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end items-center px-6 py-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="btn border-slate-200 hover:border-slate-300 text-slate-600 mr-2"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn bg-indigo-500 hover:bg-indigo-600 text-white min-w-[120px]"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                mode === "add" ? "Create Map" : "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddEditCapabilityMapModal;